import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import { LeftOutlined, RightOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Tooltip, Input } from "antd";
import SortableTree, { getFlatDataFromTree } from "react-sortable-tree";
import { createState } from "@app/components/setup/report-documents/structure/context";
import { ReportDocumentSectionStatus, ReportDocumentStatus } from "@app/constants";
import { localStore, sessionStore } from "@app/lib/storage";
import StatusIcon from "./status-icon";
import useIsTruncated from "@app/hooks/useIsTruncated";
import sessionStoreState from "@app/state/store/session";
import MaterialTheme from "@app/library/react-sortable-tree-theme-material-ui";

import reportDocumentsStore from "@app/state/store/report-document/report-documents";
import sectionsStore from "@app/state/store/report-document/report-document-sections";
import documentStore from "@app/state/store/report-document/document";

import "../styles/toc.scoped.scss";

const applyExpandedStates = (nodes, savedStates) =>
    nodes.map((node) => ({
        ...node,
        expanded: savedStates[node._id] !== undefined ? savedStates[node._id] : true,
        children: node.children ? applyExpandedStates(node.children, savedStates) : [],
    }));

const ROW_HEIGHT = 40;

// excluded sections take their children out of the navigation too
const tocTree = (nodes = []) =>
    nodes
        .filter((node) => node.toc !== false)
        .map((node) => ({ ...node, children: tocTree(node.children) }));

const TOC = observer(({ sections, appendix, documentId, editor, readonly }) => {
    const [expanded, setExpanded] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const isDraggingRef = useRef(false);

    const tocState = createState();
    const storeKey = `document.${documentId ?? ""}.toc.expanded`;
    const nodeStateKey = `document.${documentId ?? ""}.sections.expanded`;

    const getSectionStates = () => localStore.get(nodeStateKey, {});
    const saveSectionState = (sectionId, isExpanded) => {
        localStore.set(nodeStateKey, { ...getSectionStates(), [sectionId]: isExpanded });
    };

    const reportDocument = reportDocumentsStore.reportDocument;
    const canReorder =
        !readonly &&
        sessionStoreState.can("section.edit") &&
        [ReportDocumentStatus.NOT_STARTED, ReportDocumentStatus.IN_PROGRESS].includes(
            reportDocument?.status,
        );

    // True while any section rename POST or the section-order POST is in flight.
    // Disables both drag-to-reorder and title editing for the duration of the request.
    const isTocLocked =
        reportDocumentsStore.savingSections || Object.values(sectionsStore.saving).some(Boolean);

    useEffect(() => {
        // Do not rebuild the tree while a drag is in progress
        if (sections && !isDraggingRef.current) {
            let data = tocState.convertTOCToTree(sections);
            data = applyExpandedStates(data, getSectionStates());
            setTreeData(data);
        }
    }, [sections]);

    function show() {
        setExpanded(true);
        sessionStore.set(storeKey, true);

        // Keep editor focus but don't scroll the editing section back into view
        editor && editor.chain().focus(null, { scrollIntoView: false }).run();
    }

    function hide() {
        setExpanded(false);
        sessionStore.set(storeKey, false);

        // Keep editor focus but don't scroll the editing section back into view
        editor && editor.chain().focus(null, { scrollIntoView: false }).run();
    }

    useEffect(() => {
        if (documentId) {
            const expanded = sessionStore.get(storeKey, false);
            setExpanded(expanded);
        }
    }, [documentId]);

    const getDisplayPos = (treeLevel, currentPos) =>
        treeLevel.slice(0, currentPos).filter((d) => d.displayPos).length;

    const getSortKey = (index, parent) => {
        return `${parent ? parent + "/" : ""}` + String(index).padStart(3, "0");
    };

    const getTreeDataChange = (newTreeData) => {
        const getTreeNodeChange = (node, pos, displayPos, parentPos = null) => {
            node.displayPos = node?.displayPos ? displayPos : "";
            node.parent = parentPos;

            if (node?.children?.length > 0) {
                node.children = node.children.map((child, i) => {
                    if (node.toc === false) {
                        child.toc = false;
                    }
                    child.displayPos = node.displayPos ? child.displayPos : "";

                    return getTreeNodeChange(
                        child,
                        getSortKey(i, pos),
                        `${displayPos}${getDisplayPos(node.children, i) + 1}.`,
                        pos,
                    );
                });
            }

            return { ...node, pos };
        };

        return newTreeData.map((data, i) =>
            getTreeNodeChange(data, getSortKey(i), `${getDisplayPos(newTreeData, i) + 1}.`, null),
        );
    };

    const convertTreeToFlatData = (data) => {
        return getFlatDataFromTree({
            treeData: data,
            getNodeKey: ({ node }) => node.pos,
            ignoreCollapsed: false,
        }).map(({ node, path }) => ({
            _id: node?._id,
            title: node.title,
            pos: node.pos,
            toc: node.toc,
            displayPos: node.displayPos,
            parent: path.length > 1 ? path[path.length - 2] : null,
        }));
    };

    const onTreeChange = async (newTreeData) => {
        if (isTocLocked) {
            return;
        }

        const updatedTreeData = getTreeDataChange(newTreeData);
        setTreeData(updatedTreeData);

        const flatData = convertTreeToFlatData(updatedTreeData);
        const sectionsToSave = flatData.map((section) => {
            const parentSection = flatData.find((s) => s.pos === section.parent);
            return {
                _id: section._id,
                title: section.title,
                pos: section.pos,
                displayPos: section.displayPos,
                toc: section.toc,
                parent: parentSection?._id || null,
            };
        });

        // Check if there are actual changes before saving
        const hasChanges = sectionsToSave.some((newSection) => {
            const oldSection = sections.find((s) => s._id === newSection._id);
            if (!oldSection) {
                return true;
            }
            return (
                oldSection.pos !== newSection.pos ||
                oldSection.displayPos !== newSection.displayPos ||
                oldSection.parent !== newSection.parent
            );
        });

        if (!hasChanges) {
            return;
        }

        await reportDocumentsStore.saveReportDocumentSections(documentId, sectionsToSave);
        sectionsStore.sections = sectionsToSave.map((s) => {
            const existing = sectionsStore.sections.find((es) => es._id === s._id);
            return existing ? { ...existing, ...s } : s;
        });

        // Update documentStore.sections for cross-references to reflect new order
        documentStore.updateSectionOrder(sectionsToSave);
    };

    if (!sections) {
        return null;
    }

    return (
        <div className={classNames("toc", { minimized: !expanded, expanded, saving: isTocLocked })}>
            <div className="toggle-button">
                {expanded && (
                    <Tooltip title="Hide the document navigation" placement="right">
                        <Button icon={<LeftOutlined />} className="ant-btn-icon" onClick={hide} />
                    </Tooltip>
                )}

                {!expanded && (
                    <Tooltip title="Show the document navigation" placement="right">
                        <Button icon={<RightOutlined />} className="ant-btn-icon" onClick={show} />
                    </Tooltip>
                )}
            </div>

            <div className="content">
                <div className="header">
                    <em>Document Sections</em>
                    {isTocLocked && <span className="saving-indicator">Saving...</span>}
                </div>

                {canReorder ? (
                    <div className="sections sortable-sections">
                        {treeData.length > 0 && (
                            <SortableTree
                                treeData={treeData}
                                onChange={onTreeChange}
                                // Disable drag while any section rename or reorder POST is
                                // in flight to prevent concurrent requests corrupting the list.
                                canDrag={!isTocLocked}
                                onDragStateChanged={({ isDragging }) => {
                                    isDraggingRef.current = isDragging;
                                }}
                                generateNodeProps={({ node }) => {
                                    const toggleNode = () => {
                                        const newExpanded = !node.expanded;
                                        saveSectionState(node._id, newExpanded);
                                        setTreeData((prev) => {
                                            const findAndToggle = (nodes, nodeId) => {
                                                return nodes.map((n) => {
                                                    if (n._id === nodeId) {
                                                        return { ...n, expanded: !n.expanded };
                                                    }
                                                    if (n.children) {
                                                        return {
                                                            ...n,
                                                            children: findAndToggle(
                                                                n.children,
                                                                nodeId,
                                                            ),
                                                        };
                                                    }
                                                    return n;
                                                });
                                            };
                                            return findAndToggle(prev, node._id);
                                        });
                                    };

                                    return {
                                        title: (
                                            <SectionNode
                                                data={node}
                                                sections={sections}
                                                readonly={readonly}
                                                locked={isTocLocked}
                                                onToggle={
                                                    node.children?.some(
                                                        (child) => child.toc !== false,
                                                    )
                                                        ? toggleNode
                                                        : null
                                                }
                                            />
                                        ),
                                    };
                                }}
                                isVirtualized={false}
                                theme={MaterialTheme}
                                rowHeight={({ node }) => (node.toc === false ? 0 : ROW_HEIGHT)}
                                scaffoldBlockPxWidth={15}
                            />
                        )}
                        {appendix.hasAppendix && (
                            <>
                                <div className="appendix-separator" />
                                <RenderSection
                                    root={true}
                                    documentId={documentId}
                                    data={{
                                        _id: "appendix",
                                        pos: `${treeData?.length + 1}`,
                                        showStatus: false,
                                        title: "Generated Content",
                                        children: [
                                            appendix.hasTables
                                                ? {
                                                      _id: "tables",
                                                      pos: `${treeData?.length + 1}.1`,
                                                      showStatus: false,
                                                      title: "List of Tables",
                                                  }
                                                : null,
                                            appendix.hasFigures
                                                ? {
                                                      _id: "figures",
                                                      pos: `${treeData?.length + 1}.1`,
                                                      showStatus: false,
                                                      title: "List of Figures",
                                                  }
                                                : null,
                                        ].filter((x) => x),
                                    }}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <div className="sections">
                        {tocTree(treeData).map((data) => (
                            <RenderSection
                                key={data._id}
                                root={true}
                                data={data}
                                documentId={documentId}
                            />
                        ))}
                        {appendix.hasAppendix && (
                            <>
                                <div className="appendix-separator" />
                                <RenderSection
                                    root={true}
                                    documentId={documentId}
                                    data={{
                                        _id: "appendix",
                                        pos: `${treeData?.length + 1}`,
                                        showStatus: false,
                                        title: "Generated Content",
                                        children: [
                                            appendix.hasTables
                                                ? {
                                                      _id: "tables",
                                                      pos: `${treeData?.length + 1}.1`,
                                                      showStatus: false,
                                                      title: "List of Tables",
                                                  }
                                                : null,
                                            appendix.hasFigures
                                                ? {
                                                      _id: "figures",
                                                      pos: `${treeData?.length + 1}.1`,
                                                      showStatus: false,
                                                      title: "List of Figures",
                                                  }
                                                : null,
                                        ].filter((x) => x),
                                    }}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

const SectionNode = observer(({ data, sections, onToggle, readonly, locked }) => {
    const history = useHistory();
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(data.title);
    const [titleRef, isTruncated] = useIsTruncated(data.title);

    const originalSection = sections?.find((s) => s._id === data._id);
    const userReviewer = reportDocumentsStore.getUserReviewer(originalSection?.reviewers);
    const reportDocument = reportDocumentsStore.reportDocument;

    const canEdit =
        !readonly &&
        sessionStoreState.can("section.edit") &&
        [ReportDocumentStatus.NOT_STARTED, ReportDocumentStatus.IN_PROGRESS].includes(
            reportDocument?.status,
        );

    const select = () => {
        if (!isEditing) {
            history.push({
                pathname: history.location.pathname,
                search: `?section=${data._id}`,
            });
        }
    };

    const handleToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onToggle) {
            onToggle();
        }
    };

    const handleDoubleClick = (e) => {
        if (canEdit && !locked) {
            e.preventDefault();
            e.stopPropagation();
            setEditTitle(data.title);
            setIsEditing(true);
        }
    };

    const saveTitle = async () => {
        const newTitle = editTitle.trim();
        if (newTitle && newTitle !== data.title) {
            await sectionsStore.update(data._id, { title: newTitle });
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            saveTitle();
        } else if (e.key === "Escape") {
            setIsEditing(false);
            setEditTitle(data.title);
        }
    };

    let status = originalSection?.status || data.status;
    let showStatus = data.showStatus ?? true;
    const hasChildren = !!onToggle;

    const tooltipTitle = [
        isTruncated && (
            <div>
                {data.title} <hr />
            </div>
        ),
        canEdit && <div className="text-center">Double-click to edit</div>,
    ].filter(Boolean);

    if (
        userReviewer?.reviewed ||
        reportDocument?.status === ReportDocumentStatus.REVIEWED ||
        reportDocument?.status === ReportDocumentStatus.COMPLETED
    ) {
        status = ReportDocumentSectionStatus.REVIEWED;
    }

    return (
        <div className="sortable-section-node" onClick={select}>
            {showStatus && (
                <StatusIcon
                    status={status}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                />
            )}
            <label className="sectionNumber">{data.displayPos}</label>
            {isEditing ? (
                <Input
                    className="title-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <Tooltip
                    title={tooltipTitle}
                    placement={isTruncated ? "topLeft" : "top"}
                    trigger={["hover", "focus"]}
                >
                    <div
                        ref={titleRef}
                        className="sectionTitle"
                        onDoubleClick={handleDoubleClick}
                        tabIndex={isTruncated ? 0 : undefined}
                        style={{ cursor: canEdit ? "pointer" : "default" }}
                    >
                        {data.title}
                    </div>
                </Tooltip>
            )}
            <span className="toggle-spacer">
                {hasChildren && (
                    <Button
                        className="toggle-button"
                        icon={data.expanded ? <UpOutlined /> : <DownOutlined />}
                        onClick={handleToggle}
                        size="small"
                        type="text"
                    />
                )}
            </span>
        </div>
    );
});

const Section = observer(({ data, level, expandedButton, children }) => {
    const history = useHistory();
    const userReviewer = reportDocumentsStore.getUserReviewer(data.reviewers);
    const reportDocument = reportDocumentsStore.reportDocument;
    const [titleRef, isTruncated] = useIsTruncated(data.title);

    const select = () => {
        history.push({
            pathname: history.location.pathname,
            search: `?section=${data._id}`,
        });
    };

    let status = data.status;
    let showStatus = data.showStatus ?? true;

    if (
        userReviewer?.reviewed ||
        reportDocument?.status === ReportDocumentStatus.REVIEWED ||
        reportDocument?.status === ReportDocumentStatus.COMPLETED
    ) {
        status = ReportDocumentSectionStatus.REVIEWED;
    }

    return (
        <>
            <div id={data.pos} className={`sectionContainer level-${level}`} onClick={select}>
                {showStatus && (
                    <StatusIcon
                        status={status}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                    />
                )}
                <label className="sectionNumber">{data.displayPos}</label>
                <Tooltip
                    title={isTruncated ? data.title : ""}
                    placement="topLeft"
                    trigger={["hover", "focus"]}
                >
                    <div
                        ref={titleRef}
                        className="sectionTitle"
                        tabIndex={isTruncated ? 0 : undefined}
                    >
                        {data.title}
                    </div>
                </Tooltip>
                {expandedButton}
            </div>
            {children}
        </>
    );
});

const RenderSection = observer(({ root, data, numberOnly, level = 1, documentId }) => {
    const sectionStateKey = documentId ? `document.${documentId}.sections.expanded` : null;

    const [expanded, setExpanded] = useState(() => {
        if (!sectionStateKey || !data?._id) {
            return true;
        }
        const states = localStore.get(sectionStateKey, {});
        return states[data._id] !== undefined ? states[data._id] : true;
    });

    const handleToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        if (sectionStateKey && data?._id) {
            const states = localStore.get(sectionStateKey, {});
            localStore.set(sectionStateKey, { ...states, [data._id]: newExpanded });
        }
    };

    const expandedButton =
        root && data?.children?.length > 0 && !numberOnly ? (
            <span className="expanedButton">
                <Button
                    icon={expanded ? <UpOutlined /> : <DownOutlined />}
                    onClick={handleToggle}
                />
            </span>
        ) : null;

    if (expanded && data?.children?.length > 0) {
        return (
            <Section data={data} level={level} expandedButton={expandedButton}>
                {data.children.map((child) => (
                    <RenderSection
                        key={child._id}
                        root={false}
                        data={child}
                        numberOnly={numberOnly}
                        level={level + 1}
                        documentId={documentId}
                    />
                ))}
            </Section>
        );
    }

    return <Section data={data} level={level} expandedButton={expandedButton} />;
});

TOC.displayName = "TOC";

export default TOC;
